import { useUser } from "@clerk/tanstack-react-start";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { clearUser, setContext, setTag, setUser } from "@/lib/glitchtip";
import { requireAuthenticatedRoute } from "@/lib/auth-routing";
import { seoHead } from "@/lib/seo";
import { DashboardShell } from "../../components/dashboard/layout";
import { ProtectedRouteError } from "../../components/dashboard/protected-route-state";
import { CornerDots } from "../../components/dashboard/shared";
import { DASHBOARD_FONT_LINKS } from "../../components/dashboard/shared";

export const Route = createFileRoute("/dashboard")({
  head: () => {
    const head = seoHead({
      title: "Dashboard",
      description:
        "Manage your Bavimail account, domains, inboxes, webhooks, analytics, and settings.",
      path: "/dashboard/",
      noindex: true,
    });

    return {
      ...head,
      links: [...head.links.filter((link) => link.rel === "canonical"), ...DASHBOARD_FONT_LINKS],
    };
  },
  staleTime: 60_000,
  preloadStaleTime: 60_000,
  pendingMs: 120,
  pendingMinMs: 220,
  pendingComponent: DashboardPendingRoute,
  errorComponent: ProtectedRouteError,
  loader: async ({ location }) => await requireAuthenticatedRoute(location.href),
  component: DashboardRouteComponent,
});

function DashboardRouteComponent() {
  const { auth } = Route.useLoaderData();
  const { user } = useUser();

  useEffect(() => {
    if (auth?.userId) {
      void setUser({
        id: auth.userId,
        email: user?.primaryEmailAddress?.emailAddress,
        name: user?.fullName,
      });
      void setTag({ key: "auth.provider", value: "clerk" });
      void setContext({
        name: "auth",
        context: {
          provider: "clerk",
          sessionId: auth.sessionId,
        },
      });
      return;
    }

    void clearUser();
  }, [auth, user]);

  return (
    <DashboardShell>
      <Outlet />
    </DashboardShell>
  );
}

function DashboardPendingRoute() {
  return (
    <DashboardShell>
      <div className="flex h-full flex-1 flex-col overflow-hidden bg-hb-page">
        <div className="border-b border-dashed border-hb-border px-6 py-5 sm:px-8">
          <div className="h-3 w-24 animate-pulse rounded-full bg-hb-card/80" />
          <div className="mt-3 h-8 w-48 animate-pulse rounded-full bg-hb-card" />
        </div>

        <div className="grid flex-1 gap-6 overflow-hidden p-6 sm:p-8 xl:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
          <section className="relative overflow-hidden border border-dashed border-hb-card bg-hb-surface">
            <CornerDots />
            <div className="space-y-3 border-b border-dashed border-hb-border px-5 py-5">
              <div className="h-3 w-20 animate-pulse rounded-full bg-hb-card/80" />
              <div className="h-10 w-full animate-pulse rounded-full bg-hb-card" />
            </div>
            <div className="space-y-3 px-4 py-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="rounded-sm border border-dashed border-hb-border/70 px-4 py-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <div className="h-3 w-28 animate-pulse rounded-full bg-hb-card/80" />
                      <div className="h-4 w-40 animate-pulse rounded-full bg-hb-card" />
                    </div>
                    <div className="h-3 w-10 animate-pulse rounded-full bg-hb-card/70" />
                  </div>
                  <div className="mt-3 h-3 w-full animate-pulse rounded-full bg-hb-card/70" />
                </div>
              ))}
            </div>
          </section>

          <section className="relative hidden overflow-hidden border border-dashed border-hb-card bg-hb-surface xl:block">
            <CornerDots />
            <div className="border-b border-dashed border-hb-border px-6 py-5">
              <div className="h-4 w-56 animate-pulse rounded-full bg-hb-card" />
              <div className="mt-3 h-3 w-32 animate-pulse rounded-full bg-hb-card/80" />
            </div>
            <div className="space-y-4 px-6 py-6">
              <div className="h-24 animate-pulse rounded-sm bg-hb-card/80" />
              <div className="h-32 animate-pulse rounded-sm bg-hb-card/70" />
              <div className="h-24 animate-pulse rounded-sm bg-hb-card/60" />
            </div>
          </section>
        </div>
      </div>
    </DashboardShell>
  );
}
